import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { STAFF_ROLES } from '@reos/policy';
import { useT } from '../lib/i18n.js';
import { useSession } from '../stores/session.js';
import { errorMessage } from '../lib/properties.js';
import { inviteMember, listMembers, updateMember } from '../lib/members-api.js';
import { getOfficeSeats, listOffices, type OfficeDto } from '../lib/offices-api.js';

const TEAM_QUERY_KEY = ['nastaveni', 'team'] as const;
const TEAM_OFFICES_QUERY_KEY = ['nastaveni', 'team', 'offices'] as const;
const TEAM_SEATS_QUERY_KEY = ['nastaveni', 'team', 'seats'] as const;

/**
 * Krok CEN2 — „v tarifu X z Y, dalsi za Z Kc". Vsechna tri cisla pocita
 * SERVER (`GET /v1/crm/office-seats` nad cenikem a cleny kancelare), karta je
 * jen vypisuje: kdyby si pocet „nad limit" odvozovala sama ze seznamu clenu,
 * mohla by rict jine cislo, nez kolik se doucetuje.
 */
function seatsCurrency(code: string | null): string {
  return code ?? 'CZK';
}

/**
 * Krok QAFIX4b bod 2 — Nastavení → Kancelář → „Tým": kdo v kanceláři je
 * (jméno, e-mail, role, pobočka, stav), pozvánka nového člena, změna role
 * a deaktivace.
 *
 * Do tohoto kroku nevedla na pozvánku ŽÁDNÁ cesta v UI a server ji odmítal
 * (nález N03: `400 ROLE_NOT_FOUND`), takže členy pilotní kanceláře zakládal
 * ruční zásah do DB.
 *
 * JE TO KARTA NA ZÁLOŽCE „KANCELÁŘ", NE ŠESTÁ ZÁLOŽKA — `nastaveni.spec.tsx`
 * je chráněné měřidlo na PĚT záložek (stejný důvod má u sebe zapsaný
 * „Pobočky"/POB1).
 *
 * Člen se NEMAŽE, deaktivuje se: řádek členství nese historii zakázek a
 * `audit_events`. Server deaktivovanému účtu nedá tenant kontext
 * (`resolveRequestIdentity`), takže se nepřihlásí do kanceláře.
 */
export function TeamCard(): React.JSX.Element {
  const t = useT();
  const user = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = (user?.roles ?? []).some((r) => r === 'owner' || r === 'admin');

  const members = useQuery({ queryKey: TEAM_QUERY_KEY, queryFn: listMembers });
  const seats = useQuery({ queryKey: TEAM_SEATS_QUERY_KEY, queryFn: getOfficeSeats });
  const offices = useQuery({ queryKey: TEAM_OFFICES_QUERY_KEY, queryFn: () => listOffices(false) });

  const [note, setNote] = React.useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [inviteLink, setInviteLink] = React.useState<string | null>(null);

  async function reload(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
  }

  /** Jedna obálka kolem zápisu: hláška i `busy` se jinak rozcházejí po větvích. */
  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      await action();
      await reload();
      setNote({ ok: true, text: t('webAdmin.settings.team.saved') });
    } catch (e) {
      setNote({ ok: false, text: t('webAdmin.settings.team.error', { error: errorMessage(e) }) });
    } finally {
      setBusy(false);
    }
  }

  const officeList = offices.data ?? [];
  const officeName = (officeId: string | null): string =>
    officeList.find((o) => o.id === officeId)?.name ?? t('webAdmin.settings.team.officeNone');

  const items = members.data ?? [];

  return (
    <div className="rh-card" style={{ maxWidth: 640, padding: 16, marginBottom: 16 }} data-testid="nastaveni-team-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.team.title')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.team.perex')}</p>

      {/*
        Krok CEN2: tarif bez limitu makleru (`includedUsers === null`) o nem
        MLCI — „z null" by nebyla veta a „neomezeno" by byl slib, ktery cenik
        nedava. Cena dalsiho maklera se pise jen u tarifu, ktery ho prodava.
      */}
      {seats.data?.includedUsers == null ? null : (
        <p className="mb-3 text-sm text-gray-700" data-testid="team-seats">
          {t('webAdmin.settings.team.seats', {
            used: seats.data.activeUsers,
            included: seats.data.includedUsers,
          })}
          {seats.data.extraUserPriceNet === null
            ? ''
            : ` ${t('webAdmin.settings.team.seatsExtraPrice', {
                price: new Intl.NumberFormat('cs-CZ', {
                  style: 'currency',
                  currency: seatsCurrency(seats.data.currency),
                  maximumFractionDigits: 0,
                }).format(Number(seats.data.extraUserPriceNet)),
              })}`}
          {seats.data.extraUsers > 0 ? (
            <span className="ml-1 text-gray-600" data-testid="team-seats-over">
              {t('webAdmin.settings.team.seatsOver', { count: seats.data.extraUsers })}
            </span>
          ) : null}
        </p>
      )}

      <ul className="mb-3 list-none p-0" data-testid="team-list">
        {items.map((m) => (
          <li key={m.userId} className="mb-2 rounded border p-2" data-testid={`team-row-${m.userId}`}>
            {/* Mobil 390: jméno a ovládání se zalomí pod sebe (flex-wrap). */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium" data-testid={`team-name-${m.userId}`}>
                {m.name}
                <span className="ml-2 text-xs text-gray-600" data-testid={`team-email-${m.userId}`}>
                  {m.email}
                </span>
              </span>
              <span
                className="text-xs text-gray-600"
                data-testid={`team-status-${m.userId}`}
              >
                {m.deactivatedAt === null
                  ? t('webAdmin.settings.team.activeBadge')
                  : t('webAdmin.settings.team.deactivatedBadge')}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <RoleSelect
                value={m.roles[0] ?? 'broker'}
                disabled={!isOwnerOrAdmin || busy || m.userId === user?.id}
                testId={`team-role-${m.userId}`}
                onChange={(role) => void run(() => updateMember(m.userId, { role }).then(() => undefined))}
              />
              <OfficeSelect
                offices={officeList}
                value={m.officeId}
                disabled={!isOwnerOrAdmin || busy}
                testId={`team-office-${m.userId}`}
                onChange={(officeId) =>
                  void run(() => updateMember(m.userId, { officeId }).then(() => undefined))
                }
              />
              {isOwnerOrAdmin && m.userId !== user?.id ? (
                <button
                  type="button"
                  className="rh-a2-link text-xs"
                  disabled={busy}
                  data-testid={`team-deactivate-${m.userId}`}
                  onClick={() =>
                    void run(() =>
                      updateMember(m.userId, { active: m.deactivatedAt !== null }).then(() => undefined),
                    )
                  }
                >
                  {m.deactivatedAt === null
                    ? t('webAdmin.settings.team.deactivateButton')
                    : t('webAdmin.settings.team.reactivateButton')}
                </button>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-gray-600" data-testid={`team-summary-${m.userId}`}>
              {officeName(m.officeId)}
            </p>
          </li>
        ))}
      </ul>

      {items.length === 0 && !members.isLoading ? (
        <p className="mb-3 text-sm text-gray-600" data-testid="team-empty">
          {t('webAdmin.settings.team.empty')}
        </p>
      ) : null}

      {isOwnerOrAdmin ? (
        <InviteForm
          offices={officeList}
          busy={busy}
          onInvite={async (input) => {
            setBusy(true);
            setNote(null);
            try {
              const result = await inviteMember(input);
              setInviteLink(result.acceptUrl);
              await reload();
              /*
               * Pozvánka platí i když e-mail neodešel (server ji v tom
               * případě nezahodí) — hláška to musí říct, jinak by vedoucí
               * čekal na e-mail, který nepřijde, a poslal druhou pozvánku.
               */
              setNote({
                ok: true,
                text: result.emailSent
                  ? t('webAdmin.settings.team.inviteSent', { email: input.email })
                  : t('webAdmin.settings.team.inviteNoMail'),
              });
              return true;
            } catch (e) {
              setNote({ ok: false, text: t('webAdmin.settings.team.error', { error: errorMessage(e) }) });
              return false;
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : (
        <p className="text-xs text-gray-600" data-testid="team-owner-only">
          {t('webAdmin.settings.team.ownerOnly')}
        </p>
      )}

      {inviteLink !== null ? (
        <p className="mt-2 text-xs break-all" data-testid="team-invite-link">
          {inviteLink}
          <button
            type="button"
            className="rh-a2-link ml-2 text-xs"
            data-testid="team-invite-copy"
            onClick={() => {
              /* `clipboard` není v jsdom ani v http originu jistota — kopie je
                 doplněk, odkaz je vidět i tak a dá se označit myší. */
              void navigator.clipboard?.writeText(inviteLink).then(
                () => setNote({ ok: true, text: t('webAdmin.settings.team.copied') }),
                () => undefined,
              );
            }}
          >
            {t('webAdmin.settings.team.copyLink')}
          </button>
        </p>
      ) : null}

      {note !== null ? (
        <p
          className={`mt-2 text-sm ${note.ok ? 'text-green-700' : 'text-red-700'}`}
          role={note.ok ? 'status' : 'alert'}
          data-testid="team-note"
        >
          {note.text}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Výběr role. Seznam se NEOPISUJE — jede ze `STAFF_ROLES` (@reos/policy),
 * tedy z téhož zdroje, který validuje server i BetterAuth plugin. České
 * názvy jsou v katalogu `webAdmin.settings.team.roles.*`.
 */
function RoleSelect({
  value,
  disabled,
  testId,
  onChange,
}: {
  readonly value: string;
  readonly disabled: boolean;
  readonly testId: string;
  readonly onChange: (role: (typeof STAFF_ROLES)[number]) => void;
}): React.JSX.Element {
  const t = useT();
  return (
    <select
      className="rh-a2-input text-xs"
      aria-label={t('webAdmin.settings.team.roleLabel')}
      data-testid={testId}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value as (typeof STAFF_ROLES)[number])}
    >
      {STAFF_ROLES.map((role) => (
        <option key={role} value={role}>
          {t(`webAdmin.settings.team.roles.${role}`)}
        </option>
      ))}
    </select>
  );
}

function OfficeSelect({
  offices,
  value,
  disabled,
  testId,
  onChange,
}: {
  readonly offices: readonly OfficeDto[];
  readonly value: string | null;
  readonly disabled: boolean;
  readonly testId: string;
  readonly onChange: (officeId: string | null) => void;
}): React.JSX.Element {
  const t = useT();
  return (
    <select
      className="rh-a2-input text-xs"
      aria-label={t('webAdmin.settings.team.officeLabel')}
      data-testid={testId}
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    >
      <option value="">{t('webAdmin.settings.team.officeNone')}</option>
      {offices.map((office) => (
        <option key={office.id} value={office.id}>
          {office.name}
        </option>
      ))}
    </select>
  );
}

/**
 * Formulář pozvánky. Stav je lokální a resetuje se po úspěchu — po odeslání
 * zůstane na obrazovce odkaz ke zkopírování, ne rozepsaný e-mail.
 */
function InviteForm({
  offices,
  busy,
  onInvite,
}: {
  readonly offices: readonly OfficeDto[];
  readonly busy: boolean;
  readonly onInvite: (input: {
    email: string;
    role: (typeof STAFF_ROLES)[number];
    officeId?: string;
  }) => Promise<boolean>;
}): React.JSX.Element {
  const t = useT();
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<(typeof STAFF_ROLES)[number]>('broker');
  const [officeId, setOfficeId] = React.useState<string | null>(null);

  return (
    <form
      className="border-t pt-3"
      data-testid="team-invite-form"
      onSubmit={(e) => {
        e.preventDefault();
        void onInvite({
          email: email.trim(),
          role,
          ...(officeId !== null ? { officeId } : {}),
        }).then((ok) => {
          if (ok) setEmail('');
        });
      }}
    >
      <h3 className="mb-2 text-sm font-medium">{t('webAdmin.settings.team.inviteHeading')}</h3>
      {/* Mobil 390: pole pod sebou, žádný dvousloupcový grid. */}
      <label className="mb-2 block text-xs">
        {t('webAdmin.settings.team.emailLabel')}
        <input
          type="email"
          required
          className="rh-a2-input mt-1 block w-full text-sm"
          data-testid="team-invite-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <div className="mb-2 flex flex-wrap gap-2">
        <RoleSelect value={role} disabled={busy} testId="team-invite-role" onChange={setRole} />
        <OfficeSelect
          offices={offices}
          value={officeId}
          disabled={busy}
          testId="team-invite-office"
          onChange={setOfficeId}
        />
      </div>
      <button
        type="submit"
        className="rh-a2-btn rh-a2-btn--primary"
        data-testid="team-invite-submit"
        disabled={busy || email.trim() === ''}
      >
        {t('webAdmin.settings.team.inviteButton')}
      </button>
    </form>
  );
}
